import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import AssignedResourcesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/AssignedResourcesTable';
import CommandPrioritiesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CommandPrioritiesTable';
import CriticalResourceRequestsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CriticalResourceRequestsTable';
import IncidentTypeCheckboxGroup from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/IncidentTypeCheckboxGroup';
import NotableItemsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/NotableItemsTable';
import PlannedActionsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/PlannedActionsTable';
import ReportVersionToggle from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ReportVersionToggle';
import ResponderDeathsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderDeathsTable';
import ResponderInjuriesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderInjuriesTable';
import { IncidentStatusSummaryFormFields } from 'types/iapBaseTypes';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import { ICS209_BOX_LABELS } from './ics209BoxLabels';

/**
 * One of the 209's free-text boxes.
 *
 * The box's numbered title already names the field, so the label is kept for
 * screen readers and for naming the Save and Cancel buttons.
 */
const TextBoxSection = ({
  id,
  boxLabel,
  label,
  testId,
  value,
  onSave,
  readOnly,
  maxLength,
}: {
  id: string;
  boxLabel: string;
  label: string;
  testId: string;
  value?: string;
  onSave: (value: string) => void;
  readOnly?: boolean;
  maxLength?: number;
}) => (
  <Ics204CardSection boxLabel={boxLabel} testId={testId} className="tw:mt-0">
    <SaveableField
      id={id}
      label={label}
      hideLabel
      multiline
      maxLength={maxLength}
      disabled={readOnly}
      value={value}
      onSave={onSave}
    />
  </Ics204CardSection>
);

export const SituationSummaryField = ({
  statusSummaryId,
  originalSituationSummary,
  readOnly,
}: {
  statusSummaryId?: string;
  originalSituationSummary?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <TextBoxSection
      id="situationSummary"
      boxLabel={ICS209_BOX_LABELS.situationSummary}
      label="Situation Summary"
      testId="ics209-section-situation-summary"
      value={originalSituationSummary}
      readOnly={readOnly}
      onSave={(value) =>
        upsertIncidentStatusSummary({ situationSummary: value })
      }
    />
  );
};

export const LifeSafetyHealthIssuesField = ({
  statusSummaryId,
  originalLifeSafetyHealthIssues,
  readOnly,
}: {
  statusSummaryId?: string;
  originalLifeSafetyHealthIssues?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <TextBoxSection
      id="lifeSafetyHealthIssues"
      boxLabel={ICS209_BOX_LABELS.lifeSafetyHealthIssues}
      label="Life, Safety, and Health Issues"
      testId="ics209-section-life-safety-health-issues"
      value={originalLifeSafetyHealthIssues}
      readOnly={readOnly}
      onSave={(value) =>
        upsertIncidentStatusSummary({ lifeSafetyHealthIssues: value })
      }
    />
  );
};

export const MarineTransportationSystemField = ({
  statusSummaryId,
  originalMarineTransportationSystem,
  readOnly,
}: {
  statusSummaryId?: string;
  originalMarineTransportationSystem?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <TextBoxSection
      id="marineTransportationSystem"
      boxLabel={ICS209_BOX_LABELS.marineTransportationSystem}
      label="Impacts to Marine Transportation System and Critical Infrastructure"
      testId="ics209-section-marine-transportation-system"
      value={originalMarineTransportationSystem}
      readOnly={readOnly}
      maxLength={1000}
      onSave={(value) =>
        upsertIncidentStatusSummary({ marineTransportationSystem: value })
      }
    />
  );
};

export const AmplifyingInformationField = ({
  statusSummaryId,
  originalAmplifyingInformation,
  readOnly,
}: {
  statusSummaryId?: string;
  originalAmplifyingInformation?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <SaveableField
      id="amplifyingInformation"
      label="Amplifying Information"
      multiline
      maxLength={1000}
      disabled={readOnly}
      value={originalAmplifyingInformation}
      onSave={(value) =>
        upsertIncidentStatusSummary({ amplifyingInformation: value })
      }
    />
  );
};

export const CommentsField = ({
  statusSummaryId,
  originalComments,
  readOnly,
}: {
  statusSummaryId?: string;
  originalComments?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <SaveableField
      id="comments"
      label="Comments"
      multiline
      maxLength={1000}
      disabled={readOnly}
      value={originalComments}
      onSave={(value) => upsertIncidentStatusSummary({ comments: value })}
    />
  );
};

export const MediaPhotosPublicOutreachField = ({
  statusSummaryId,
  originalMediaPhotosPublicOutreach,
  readOnly,
}: {
  statusSummaryId?: string;
  originalMediaPhotosPublicOutreach?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <TextBoxSection
      id="mediaPhotosPublicOutreach"
      boxLabel={ICS209_BOX_LABELS.mediaPhotosPublicOutreach}
      label="Media, Photos, and Public Outreach"
      testId="ics209-section-media-photos-public-outreach"
      value={originalMediaPhotosPublicOutreach}
      readOnly={readOnly}
      maxLength={1000}
      onSave={(value) =>
        upsertIncidentStatusSummary({ mediaPhotosPublicOutreach: value })
      }
    />
  );
};

const IncidentStatusSummaryFormComponents = ({
  statusSummaryId,
  originalIncidentStatusSummary,
  readOnly = false,
}: {
  statusSummaryId: string;
  originalIncidentStatusSummary: IncidentStatusSummaryFormFields;
  readOnly?: boolean;
}) => (
  <>
    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.reportVersion}
      testId="ics209-section-report-version"
      className="tw:mt-0"
    >
      <ReportVersionToggle
        statusSummaryId={statusSummaryId}
        originalReportVersion={originalIncidentStatusSummary?.reportVersion}
        readOnly={readOnly}
      />
    </Ics204CardSection>

    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.incidentType}
      testId="ics209-section-incident-type"
      className="tw:mt-0"
    >
      <IncidentTypeCheckboxGroup
        statusSummaryId={statusSummaryId}
        originalIncidentType={originalIncidentStatusSummary?.incidentType}
        originalOtherIncidentType={
          originalIncidentStatusSummary?.otherIncidentType
        }
        readOnly={readOnly}
      />
    </Ics204CardSection>

    <SituationSummaryField
      statusSummaryId={statusSummaryId}
      originalSituationSummary={originalIncidentStatusSummary?.situationSummary}
      readOnly={readOnly}
    />

    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.commandPriorities}
      testId="ics209-section-command-priorities"
      className="tw:mt-0"
    >
      <CommandPrioritiesTable
        statusSummaryId={statusSummaryId}
        originalCommandPriorities={
          originalIncidentStatusSummary?.commandPriorities
        }
        readOnly={readOnly}
      />
    </Ics204CardSection>

    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.notableItems}
      testId="ics209-section-notable-items"
      className="tw:mt-0"
    >
      <NotableItemsTable
        statusSummaryId={statusSummaryId}
        originalNotableItems={originalIncidentStatusSummary?.notableItems}
        readOnly={readOnly}
      />
    </Ics204CardSection>

    {/* Box 9b prints injuries and deaths as two halves of one responder status
        block, so they share a section rather than each claiming a box. */}
    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.responderStatus}
      testId="ics209-section-responder-status"
      className="tw:mt-0"
    >
      <div className="tw:grid tw:gap-3 tw:lg:grid-cols-2">
        <ResponderInjuriesTable
          statusSummaryId={statusSummaryId}
          originalResponderInjuries={
            originalIncidentStatusSummary?.responderInjuries
          }
          readOnly={readOnly}
        />
        <ResponderDeathsTable
          statusSummaryId={statusSummaryId}
          originalResponderDeaths={
            originalIncidentStatusSummary?.responderDeaths
          }
          readOnly={readOnly}
        />
      </div>
    </Ics204CardSection>

    <LifeSafetyHealthIssuesField
      statusSummaryId={statusSummaryId}
      originalLifeSafetyHealthIssues={
        originalIncidentStatusSummary?.lifeSafetyHealthIssues
      }
      readOnly={readOnly}
    />

    <MarineTransportationSystemField
      statusSummaryId={statusSummaryId}
      originalMarineTransportationSystem={
        originalIncidentStatusSummary?.marineTransportationSystem
      }
      readOnly={readOnly}
    />

    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.criticalResourceRequests}
      testId="ics209-section-critical-resource-requests"
      className="tw:mt-0"
    >
      <CriticalResourceRequestsTable
        statusSummaryId={statusSummaryId}
        originalCriticalResourceRequests={
          originalIncidentStatusSummary?.criticalResourceRequests
        }
        readOnly={readOnly}
      />
    </Ics204CardSection>

    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.plannedActions}
      testId="ics209-section-planned-actions"
      className="tw:mt-0"
    >
      <PlannedActionsTable
        statusSummaryId={statusSummaryId}
        originalPlannedActions={originalIncidentStatusSummary?.plannedActions}
        readOnly={readOnly}
      />
    </Ics204CardSection>

    {/* Amplifying information and comments print inside box 16, under the
        assigned resources table, rather than as boxes of their own. */}
    <Ics204CardSection
      boxLabel={ICS209_BOX_LABELS.assignedResources}
      testId="ics209-section-assigned-resources"
      className="tw:mt-0"
    >
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
        <AssignedResourcesTable
          statusSummaryId={statusSummaryId}
          originalAssignedResources={
            originalIncidentStatusSummary?.assignedResources
          }
          readOnly={readOnly}
        />
        <AmplifyingInformationField
          statusSummaryId={statusSummaryId}
          originalAmplifyingInformation={
            originalIncidentStatusSummary?.amplifyingInformation
          }
          readOnly={readOnly}
        />
        <CommentsField
          statusSummaryId={statusSummaryId}
          originalComments={originalIncidentStatusSummary?.comments}
          readOnly={readOnly}
        />
      </div>
    </Ics204CardSection>

    <MediaPhotosPublicOutreachField
      statusSummaryId={statusSummaryId}
      originalMediaPhotosPublicOutreach={
        originalIncidentStatusSummary?.mediaPhotosPublicOutreach
      }
      readOnly={readOnly}
    />
  </>
);

export default IncidentStatusSummaryFormComponents;
